// src/oura/ask/text.ts
//
// Ask Oura (PREVIEW): the links into the existing screens, and the small
// helpers every answer is written with. Sentence case, no em dashes.

import { OURA_ROOT } from "../nav";
import { dayMonth } from "../visibility/format";
import type { ClientData } from "./useAskData";
import type { AskBlock, AskSentence, AskSource } from "./types";

const base = (id: string) => `${OURA_ROOT}/businesses/${encodeURIComponent(id)}`;

/** Every route an answer links to. Each one is a route or a drawer the screen already opens from its address. */
export const hrefs = {
    overview: (id: string) => base(id),
    visibility: (id: string) => `${base(id)}/questions`,
    /** Visibility with the Missing you filter on (Visibility.tsx reads `?filter=`). */
    missing: (id: string) => `${base(id)}/questions?filter=missing`,
    /** Visibility's Competitors tab (`?tab=competitors`). */
    competitors: (id: string) => `${base(id)}/questions?tab=competitors`,
    site: (id: string) => `${base(id)}/setup`,
    /** One site issue's How to fix drawer (SiteAudit.tsx reads `?issue=`). */
    issue: (id: string, key: string) => `${base(id)}/setup?issue=${encodeURIComponent(key)}`,
    pages: (id: string) => `${base(id)}/pages`,
    /** One page open in the Page workshop. `from=plan` briefly marks the Answer Plan's proposals. */
    page: (id: string, url: string, title: string, fromPlan = false) =>
        `${base(id)}/pages?page=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}${fromPlan ? "&from=plan" : ""}`,
    delivery: (id: string) => `${base(id)}/delivery`,
    /** Delivery's Plan view: key pages and the contract. */
    plan: (id: string) => `${base(id)}/delivery?view=plan`,
    /** Delivery's By month view. Hidden for now (#4727): the timeline is on Plan. */
    month: (id: string) => `${base(id)}/delivery?view=month`,
    deliverable: (id: string, key: string) => `${base(id)}/delivery?d=${encodeURIComponent(key)}`,
    client: (id: string) => `${base(id)}/reviews`,
    doc: (id: string, docId: string) => `${base(id)}/reviews?doc=${encodeURIComponent(docId)}`,
    request: (id: string, reqId: string) => `${base(id)}/reviews?request=${encodeURIComponent(reqId)}`,
    /** This week's note, unfolded with the cursor in it (ClientReviews.tsx reads `?compose=note`). */
    note: (id: string) => `${base(id)}/reviews?compose=note`,
    /**
     * Create a doc on the Documents card, with this doc type picked (`?create=`). `new` picks nothing.
     * `codes` and `outlet` are the ones Delivery would prefill for the same step (`&codes=G1`), so a doc
     * started here never claims codes another doc already clears.
     */
    create: (id: string, docType: string | null, codes: readonly string[] = [], outlet: string | null = null) =>
        `${base(id)}/reviews?create=${encodeURIComponent(docType ?? "new")}${codes.length ? `&codes=${encodeURIComponent(codes.join(","))}` : ""}${outlet ? `&outlet=${encodeURIComponent(outlet)}` : ""}`,
    history: (id: string) => `${base(id)}/history`,
    today: () => `${OURA_ROOT}/businesses?view=today`,
    allDocs: () => `${OURA_ROOT}/businesses?view=documents`,
    businesses: () => `${OURA_ROOT}/businesses`,
};

export const src = (label: string, to: string): AskSource => ({ label, to });
export const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
export const wd = (days: number | null | undefined) => (days === null || days === undefined ? null : days <= 0 ? "under 1 working day" : n(days, "working day"));
export const s = (text: string, ...cites: AskSource[]): AskSentence => ({ text, cites: cites.length ? cites : undefined });
export const p = (...sentences: AskSentence[]): AskBlock => ({ kind: "p", sentences });
/** "A, B and C". */
export const andList = (parts: string[]) => (parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);

/** The client's person, by first name where the records give one. */
export function contactOf(d: Pick<ClientData, "agreed" | "requests">): string {
    if (d.agreed?.clientName) return d.agreed.clientName;
    const r = [...(d.requests ?? [])].sort((a, b) => b.at.localeCompare(a.at))[0];
    return r?.requestedBy ?? "the client";
}

export const visSource = (d: Pick<ClientData, "vis" | "businessId">): AskSource =>
    src(d.vis ? `Visibility · scan ${dayMonth(d.vis.capturedAt)}` : "Visibility", hrefs.visibility(d.businessId));

/* Did you mean: the closest questions by shared words (no model in this preview). */

const STOP = new Set("a an the is are was were be to of in on for at by and or me my i we us our you your it its this that what whats which who how do does did can could would should will with about from them they their there here please pls oura ask client clients".split(" "));
const stems = (t: string) =>
    t
        .toLowerCase()
        .replace(/[’']/g, "")
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length > 1 && !STOP.has(w))
        .map((w) => w.slice(0, 5));

/** The two questions Ask Oura answers that share the most words with what was asked. */
export function closest(t: string, pool: string[]): string[] {
    const asked = new Set(stems(t));
    const scored = Array.from(new Set(pool)).map((q, i) => ({ q, i, score: stems(q).filter((w) => asked.has(w)).length }));
    scored.sort((a, b) => b.score - a.score || a.i - b.i);
    return scored.slice(0, 2).map((x) => x.q);
}
